import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DEFAULT_HOME_SETTINGS,
  getHomeSettings,
  resetHomeHeroImage,
  updateHomeSettings,
  uploadHomeHeroImage,
  HOME_SETTINGS_EVENT,
  type HomeSettings,
} from '@/services/siteSettingsService';

export default function AdminHomeSettingsPage() {
  const [settings, setSettings] = useState<HomeSettings>(DEFAULT_HOME_SETTINGS);
  const [heroEyebrow, setHeroEyebrow] = useState(DEFAULT_HOME_SETTINGS.heroEyebrow);
  const [heroTitle, setHeroTitle] = useState(DEFAULT_HOME_SETTINGS.heroTitle);
  const [heroButtonText, setHeroButtonText] = useState(DEFAULT_HOME_SETTINGS.heroButtonText);
  const [heroBrandText, setHeroBrandText] = useState(DEFAULT_HOME_SETTINGS.heroBrandText);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const localPreview = useMemo(
    () => selectedFile ? URL.createObjectURL(selectedFile) : '',
    [selectedFile],
  );

  useEffect(() => {
    return () => { if (localPreview) URL.revokeObjectURL(localPreview); };
  }, [localPreview]);

  useEffect(() => {
    const load = async () => {
      try {
        const current = await getHomeSettings();
        setSettings(current);
        setHeroEyebrow(current.heroEyebrow);
        setHeroTitle(current.heroTitle);
        setHeroButtonText(current.heroButtonText);
        setHeroBrandText(current.heroBrandText);
      } catch (err) {
        setError(err instanceof Error ? err.message : '설정을 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleFile = (file?: File) => {
    setError('');
    setMessage('');
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('JPG, PNG, WebP 이미지만 선택할 수 있습니다.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('이미지는 50MB 이하만 선택할 수 있습니다.');
      return;
    }
    setSelectedFile(file);
  };

  const handleSave = async () => {
    setError('');
    setMessage('');
    if (!heroEyebrow.trim() || !heroTitle.trim() || !heroButtonText.trim() || !heroBrandText.trim()) {
      setError('모든 글씨를 입력해 주세요.');
      return;
    }
    setSaving(true);
    try {
      let current = await updateHomeSettings({
        heroEyebrow: heroEyebrow.trim(),
        heroTitle: heroTitle.trim(),
        heroButtonText: heroButtonText.trim(),
        heroBrandText: heroBrandText.trim(),
      });
      if (selectedFile) {
        current = await uploadHomeHeroImage(selectedFile);
        setSelectedFile(null);
      }
      setSettings(current);
      window.dispatchEvent(new CustomEvent(HOME_SETTINGS_EVENT, { detail: current }));
      setMessage('홈 화면이 저장되었습니다. 홈에서 바로 확인할 수 있어요.');
    } catch (err) {
      setError(err instanceof Error ? err.message : '저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetImage = async () => {
    if (!window.confirm('기본 이미지로 되돌릴까요?')) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const current = await resetHomeHeroImage();
      setSettings(current);
      setSelectedFile(null);
      window.dispatchEvent(new CustomEvent(HOME_SETTINGS_EVENT, { detail: current }));
      setMessage('기본 이미지로 되돌렸습니다.');
    } catch (err) {
      setError(err instanceof Error ? err.message : '이미지를 초기화하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-foreground-500">불러오는 중...</div>;
  }

  const previewImage = localPreview || settings.heroImage;

  return (
    <div className="min-h-screen bg-background-50 pb-10">
      <header className="px-4 pt-10 pb-5 flex items-center gap-3 border-b border-background-100">
        <Link to="/admin" className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-secondary-100 cursor-pointer">
          <i className="ri-arrow-left-line text-lg"></i>
        </Link>
        <div>
          <p className="text-xs font-semibold text-accent-500">관리자 설정</p>
          <h1 className="text-xl font-heading font-bold text-foreground-950">홈 화면 꾸미기</h1>
        </div>
      </header>

      <main className="px-4 pt-5 space-y-5">
        <section className="bg-white rounded-2xl p-4">
          <h2 className="text-sm font-bold text-foreground-900 mb-3">미리보기</h2>
          <div className="relative h-[330px] rounded-xl overflow-hidden bg-secondary-100">
            <img src={previewImage} alt="홈 대표 미리보기" className="absolute inset-0 w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/40"></div>
            <div className="relative z-10 h-full px-5 pb-6 flex flex-col justify-end text-white">
              <span className="text-xs font-medium tracking-widest mb-2">{heroEyebrow || '작은 글씨'}</span>
              <h3 className="text-3xl font-heading font-bold leading-tight whitespace-pre-line mb-4">{heroTitle || '큰 제목'}</h3>
              <span className="w-fit px-4 py-2 rounded-full border border-white/40 bg-white/20 text-sm">+ {heroButtonText || '버튼 글씨'}</span>
            </div>
            <span className="absolute bottom-5 right-5 z-10 text-white/50 text-[10px] tracking-[0.25em]">{heroBrandText || '오른쪽 아래 글씨'}</span>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-4 space-y-3">
          <div>
            <h2 className="text-sm font-bold text-foreground-900">대표 이미지</h2>
            <p className="text-xs text-foreground-500 mt-1">JPG, PNG, WebP · 50MB 이하</p>
          </div>
          <label className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border border-dashed border-accent-300 text-sm font-medium text-accent-600 bg-accent-50 cursor-pointer">
            <i className="ri-image-add-line"></i>
            {selectedFile ? selectedFile.name : '새 이미지 선택'}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          </label>
          {!settings.usesDefaultImage && (
            <button type="button" onClick={handleResetImage} disabled={saving} className="w-full py-2.5 text-xs text-foreground-500 hover:text-accent-600 cursor-pointer disabled:opacity-50">
              기본 이미지로 되돌리기
            </button>
          )}
        </section>

        <section className="bg-white rounded-2xl p-4 space-y-4">
          <h2 className="text-sm font-bold text-foreground-900">화면 글씨</h2>
          <label className="block">
            <span className="text-xs text-foreground-600">작은 글씨</span>
            <input value={heroEyebrow} maxLength={60} onChange={(e) => setHeroEyebrow(e.target.value)} className="mt-1.5 w-full px-3 py-3 rounded-xl border border-background-200 text-sm outline-none focus:border-accent-400" />
          </label>
          <label className="block">
            <span className="text-xs text-foreground-600">큰 제목</span>
            <textarea value={heroTitle} maxLength={120} rows={3} onChange={(e) => setHeroTitle(e.target.value)} className="mt-1.5 w-full px-3 py-3 rounded-xl border border-background-200 text-sm outline-none resize-none focus:border-accent-400" />
            <p className="text-[11px] text-foreground-400 mt-1">줄을 바꾸고 싶은 위치에서 Enter를 누르세요.</p>
          </label>
          <label className="block">
            <span className="text-xs text-foreground-600">버튼 글씨</span>
            <input value={heroButtonText} maxLength={30} onChange={(e) => setHeroButtonText(e.target.value)} className="mt-1.5 w-full px-3 py-3 rounded-xl border border-background-200 text-sm outline-none focus:border-accent-400" />
          </label>
          <label className="block">
            <span className="text-xs text-foreground-600">오른쪽 아래 글씨</span>
            <input value={heroBrandText} maxLength={40} onChange={(e) => setHeroBrandText(e.target.value)} className="mt-1.5 w-full px-3 py-3 rounded-xl border border-background-200 text-sm outline-none focus:border-accent-400" />
          </label>
        </section>

        {error && <div className="px-4 py-3 rounded-xl bg-red-50 text-sm text-red-600">{error}</div>}
        {message && <div className="px-4 py-3 rounded-xl bg-emerald-50 text-sm text-emerald-700">{message}</div>}

        <button onClick={handleSave} disabled={saving} className="w-full py-4 rounded-xl bg-accent-500 text-white font-semibold cursor-pointer disabled:opacity-50">
          {saving ? '저장 중...' : '홈 화면 저장하기'}
        </button>
      </main>
    </div>
  );
}
